import apiService from './api';
import { LoginRequest, LoginResponse, VerifyIpLoginRequest } from '../types';
import { getOrCreateFingerprintHash, reportFingerprint } from '../utils/fingerprint';

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    console.log('🔐 authService.login called with:', { username: credentials.username });
    apiService.clearTokens();

    const attemptLogin = async () => {
      const fingerprintHash = getOrCreateFingerprintHash();
      return apiService.post<LoginResponse>('/api/Auth/login', credentials, {
        headers: {
          'X-Fingerprint': fingerprintHash,
        },
      });
    };

    try {
      let response: LoginResponse;
      try {
        response = await attemptLogin();
      } catch (error: any) {
        const isNetworkError = !error?.response && (error?.message === 'Network Error' || error?.code === 'ECONNABORTED');
        if (!isNetworkError) {
          throw error;
        }
        console.warn('Login network error; retrying...', error);
        for (const delayMs of [1200, 2500]) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          try {
            response = await attemptLogin();
            break;
          } catch (retryError: any) {
            const stillNetwork = !retryError?.response
              && (retryError?.message === 'Network Error' || retryError?.code === 'ECONNABORTED');
            if (!stillNetwork) {
              throw retryError;
            }
            if (delayMs === 2500) {
              throw retryError;
            }
          }
        }
      }
      console.log('✅ Login API response:', response);
      
      // If MFA or IP verification is required, don't set tokens - return response for UI handling
      if (response.requiresMfa || response.requiresIpVerification) {
        console.log('🔒 Secondary factor required - tokens not set');
        return response;
      }
      
      // Store tokens in API service (only when login is complete)
      apiService.setTokens(response.accessToken, response.refreshToken);
      
      // Report full fingerprint data after successful login (async, non-blocking)
      this.reportDeviceFingerprint(response.accessToken).catch(err => {
        console.warn('Failed to report fingerprint:', err);
      });
      
      return response;
    } catch (error) {
      console.error('❌ authService.login error:', error);
      throw error;
    }
  }
  
  /**
   * Complete IP verification with email code
   * Returns full login response with tokens on success
   */
  async verifyIpLogin(request: VerifyIpLoginRequest): Promise<LoginResponse> {
    console.log('🔐 authService.verifyIpLogin called');
    try {
      const response = await apiService.post<LoginResponse>('/api/Auth/verify-ip', request);
      console.log('✅ IP verification response:', response);
      
      // Store tokens after successful verification
      apiService.setTokens(response.accessToken, response.refreshToken);
      
      // Report full fingerprint data after successful login (async, non-blocking)
      this.reportDeviceFingerprint(response.accessToken).catch(err => {
        console.warn('Failed to report fingerprint:', err);
      });
      
      return response;
    } catch (error) {
      console.error('❌ authService.verifyIpLogin error:', error);
      throw error;
    }
  }

  async completeMfa(request: {
    token: string;
    method: string;
    code?: string;
    assertionResponse?: unknown;
  }): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/api/Auth/complete-mfa', request);
    if (response.requiresIpVerification) {
      return response;
    }
    if (response.accessToken) {
      apiService.setTokens(response.accessToken, response.refreshToken);
      this.reportDeviceFingerprint(response.accessToken).catch(() => undefined);
    }
    return response;
  }

  async requestMfaEmail(token: string): Promise<{ maskedEmail: string }> {
    return apiService.post<{ maskedEmail: string }>('/api/Auth/request-mfa-email', { token });
  }

  async getPasskeyAuthOptions(
    userId: string,
    passkeyId?: string,
    preferPlatform?: boolean,
    scopeCredentialId?: string | null,
    preferHybrid?: boolean
  ): Promise<any> {
    return apiService.post('/api/passkey/authenticate-options', {
      userId,
      passkeyId,
      preferPlatform: !!preferPlatform,
      preferHybrid: !!preferHybrid,
      scopeCredentialId: scopeCredentialId || undefined,
    });
  }
  
  /**
   * Report device fingerprint for trust scoring
   */
  private async reportDeviceFingerprint(accessToken: string): Promise<void> {
    const apiUrl = apiService.getBaseUrl();
    const result = await reportFingerprint(apiUrl, accessToken);
    
    if (result) {
      if (result.isNewDevice) {
        console.log('🆕 New device detected for this account');
      }
      if (result.isNewIp) {
        console.log('🌍 New IP address detected for this account');
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/api/Auth/logout');
    } finally {
      apiService.clearTokens();
    }
  }

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/api/Auth/refresh', { refreshToken });
    apiService.setTokens(response.accessToken, response.refreshToken);
    return response;
  }

  isAuthenticated(): boolean {
    return apiService.getAccessToken() !== null;
  }
}

export const authService = new AuthService();
export default authService;

